import { useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../../hooks/useAgentChat';
import AgentChart from './AgentChart';
import ConfirmationCard from './ConfirmationCard';
import CopyButton from './CopyButton';
import ToolIndicator from './ToolIndicator';

function CopyableTable({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) {
    const tableRef = useRef<HTMLTableElement>(null);

    const getTableText = useCallback(() => {
        const table = tableRef.current;
        if (!table) return '';
        const rows = Array.from(table.querySelectorAll('tr'));
        return rows.map(row => {
            const cells = Array.from(row.querySelectorAll('th, td'));
            return cells.map(c => c.textContent?.trim() ?? '').join('\t');
        }).join('\n');
    }, []);

    return (
        <div className="relative group/table">
            <table ref={tableRef} {...props}>{children}</table>
            <div className="absolute top-0 right-0 opacity-0 group-hover/table:opacity-100 transition-opacity">
                <CopyButton getText={getTableText} title="Copy table" />
            </div>
        </div>
    );
}

// The LLM occasionally emits markdown images like `![P&L Summary](url)` for
// section icons, but charts go through the dedicated chart event channel —
// any remaining markdown image is either a hallucinated URL or an empty src
// and renders as a broken-image placeholder. Fall back to the alt text in
// italics so the surrounding sentence still reads naturally.
function MarkdownImage({ alt }: React.ImgHTMLAttributes<HTMLImageElement>) {
    if (!alt) return null;
    return <em className="text-slate-600">{alt}</em>;
}

interface MessageListProps {
    messages: ChatMessage[];
    isLoading: boolean;
    isThinking: boolean;
    onConfirm: (toolCallId: string) => void;
    onConfirmAll: (toolCallId: string) => void;
    onCancel: (toolCallId: string) => void;
}

export default function MessageList({ messages, isLoading, isThinking, onConfirm, onConfirmAll, onCancel }: MessageListProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const endRef = useRef<HTMLDivElement>(null);
    const isNearBottomRef = useRef(true);
    const prevMessageCountRef = useRef(0);

    // Track whether user is near the bottom of the scroll container
    const handleScroll = useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const threshold = 80;
        isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    }, []);

    useEffect(() => {
        // Always scroll on new user message (message count increased)
        const hasNewMessage = messages.length > prevMessageCountRef.current;
        prevMessageCountRef.current = messages.length;

        if (hasNewMessage || isNearBottomRef.current) {
            endRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading, isThinking]);

    if (messages.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="text-slate-400 mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                    </div>
                    <p className="text-sm text-slate-500">Ask me about your financial model</p>
                    <p className="text-xs text-slate-400 mt-1">I can help you explore, modify, and get insights from your financial model</p>
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages
                .filter(msg => {
                    if (msg.content?.trim() || msg.charts?.length || msg.confirmation) return true;
                    // Keep streaming assistant bubbles visible even when they
                    // currently have no content and no active tool — the
                    // bubble shows a "working" placeholder so the thread
                    // doesn't jump while we wait between tool calls.
                    if (msg.role === 'assistant' && msg.isStreaming) return true;
                    if (!msg.toolCalls?.length) return false;
                    return msg.toolCalls.some(tc => tc.status === 'error');
                })
                .map(msg => {
                    const hasCharts = !!(msg.charts?.length);
                    // The in-bubble "working" placeholder fires only while
                    // the bubble has NO content at all (not even whitespace),
                    // no active tool indicator, no confirmation card. This
                    // covers two cases: (1) the initial wait before any
                    // event arrives, (2) the wait after a chart is generated
                    // before the next iteration produces anything.
                    // As soon as the first character (even a newline) lands
                    // in the bubble, the placeholder retires — the next event
                    // is already in flight and either the chart-split logic
                    // will move on, or the tool/content will replace it.
                    const hasAnyToolCall = !!(msg.toolCalls && msg.toolCalls.length > 0);
                    const showWorkingPlaceholder =
                        msg.role === 'assistant' &&
                        msg.isStreaming &&
                        !msg.content?.length &&
                        !msg.confirmation &&
                        !hasAnyToolCall;
                    return (
                <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} relative hover:z-10`}
                >
                    <div
                        className={`${hasCharts ? 'w-full' : 'max-w-[85%]'} rounded-lg px-3 py-2 text-sm ${
                            msg.role === 'user'
                                ? 'bg-primary text-white'
                                : 'bg-slate-100 text-slate-800'
                        } group/msg relative`}
                    >
                        {/* Copy button — overlaps top-right edge */}
                        {msg.content?.trim() && !msg.isStreaming && (
                            <div className="absolute -top-3.5 right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10">
                                <div className="bg-white border border-slate-200 rounded-md shadow-sm">
                                    <CopyButton getText={() => msg.content} title="Copy message" />
                                </div>
                            </div>
                        )}

                        {/* Message text */}
                        {msg.content && (
                            <div className={`leading-relaxed ${msg.role === 'assistant' ? 'agent-markdown' : ''}`}>
                                {msg.role === 'assistant' ? (
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        components={{ table: CopyableTable, img: MarkdownImage }}
                                    >
                                        {msg.content}
                                    </ReactMarkdown>
                                ) : (
                                    <span className="whitespace-pre-wrap">{msg.content}</span>
                                )}
                            </div>
                        )}

                        {/* Tool call indicators: while streaming, show the
                            last tool indicator only as long as no text content
                            has arrived yet. Once any content is in the bubble,
                            the text itself is the user feedback and the
                            spinner just becomes visual noise that lingers
                            until the stream ends. Errors are always shown. */}
                        {msg.toolCalls
                            ?.filter((tc, i, arr) =>
                                tc.status === 'error' || (msg.isStreaming && i === arr.length - 1 && !msg.content)
                            )
                            .map(tc => (
                                <ToolIndicator
                                    key={tc.tool_call_id}
                                    toolName={tc.tool_name}
                                    description={tc.description}
                                    status={tc.status}
                                />
                            ))}

                        {/* Inline charts */}
                        {msg.charts?.map((chart, idx) => (
                            <AgentChart key={`chart-${msg.id}-${idx}`} chart={chart} />
                        ))}

                        {/* Working placeholder — sits at the bottom of the
                            bubble so it appears below any chart, marking the
                            thread as still in progress while the model
                            decides what to do next. */}
                        {showWorkingPlaceholder && (
                            <div className="flex items-center gap-2 text-xs text-slate-500 py-1" aria-label="Agent is working">
                                <svg className="animate-spin h-3 w-3 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                <span className="inline-flex items-center gap-0.5">
                                    <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1 h-1 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </span>
                            </div>
                        )}

                        {/* Confirmation card */}
                        {msg.confirmation && (
                            <ConfirmationCard
                                confirmation={msg.confirmation}
                                onConfirm={() => onConfirm(msg.confirmation!.tool_call_id)}
                                onConfirmAll={() => onConfirmAll(msg.confirmation!.tool_call_id)}
                                onCancel={() => onCancel(msg.confirmation!.tool_call_id)}
                                disabled={isLoading}
                            />
                        )}
                    </div>
                </div>
            );
            })}
            {/* Typing indicator — shown only when no streaming assistant
                bubble is on screen (those have their own in-bubble working
                placeholder, so this would be a duplicate). */}
            {isThinking && !messages.some(msg => msg.role === 'assistant' && msg.isStreaming) && (
                <div className="flex justify-start">
                    <div className="rounded-lg px-3 py-2 bg-slate-100">
                        <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                    </div>
                </div>
            )}
            <div ref={endRef} />
        </div>
    );
}
